import {
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import { Flex, Menu, type MenuProps } from 'antd';
import {
  getAllNodeInfos,
  getLBNodeType,
  getNodeInfoForType,
  NodeType,
} from './registry';
import { isValidChildType, type ChildTypeValidity } from '../graphUtils';
import {
  getCategoryLabel,
  NodeCategory,
  OutputType,
  type LBNodeInfo,
  type ReactFlowEdge,
  type ReactFlowNode,
} from '../types';
import { wouldChildSeeShipmentVariable } from '../variables/variableUtils';
import '../LogiBlocks.scss';
import styles from './NodePicker.module.css';
import { CustomShipmentText, SearchInput } from '@/components';

type NodePickerEntry = { info: LBNodeInfo; validity: ChildTypeValidity };

const ICON_BACKGROUND_CLASS: Partial<Record<NodeCategory, string>> = {
  [NodeCategory.CONTROL_FLOW]: styles.iconControlFlow,
  [NodeCategory.ACTION]: styles.iconAction,
};

export interface NodePickerProps {
  /** The ID of the parent node */
  parentNodeId: string;

  /** Callback when user selects a node type */
  onSelect: (nodeType: NodeType) => void;

  /** Whether the picker is open */
  open: boolean;

  /** Callback when picker should close */
  onClose: () => void;

  /**
   * Position to anchor the dropdown.
   * - x, y: screen coordinates
   * - alignRight: if true, the picker's right edge aligns with x (used when positioned left of button)
   */
  anchorPosition?: { x: number; y: number; alignRight?: boolean };
}

/**
 * Dropdown picker for selecting a node type to add as a child.
 * Filters options based on what's valid for the given parent type.
 */
export function NodePicker({
  parentNodeId,
  onSelect,
  open,
  onClose,
  anchorPosition,
}: NodePickerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const rfNodes = useNodes<ReactFlowNode>();
  const rfEdges = useEdges<ReactFlowEdge>();
  const [searchText, setSearchText] = useState('');

  // Get visible node types grouped by category (includes disabled options)
  const groupedOptions = useMemo(() => {
    const parentNode = rfNodes.find((n) => n.id === parentNodeId);
    const parentType = parentNode ? getLBNodeType(parentNode.data) : undefined;
    if (!parentType) return new Map<NodeCategory, NodePickerEntry[]>();

    const isInsideSubgraph = parentNode?.parentId != null;
    const graphHasSubgraph = rfNodes.some((n) => {
      const nodeType = getLBNodeType(n.data);
      return nodeType
        ? getNodeInfoForType(nodeType).outputType === OutputType.SUBGRAPH_PARENT
        : false;
    });

    // Group by category, excluding hidden entries
    const grouped = new Map<NodeCategory, NodePickerEntry[]>();
    for (const info of getAllNodeInfos()) {
      let validity = isValidChildType({
        parentType,
        childType: info.protobufNodeDataKey,
        isInsideSubgraph,
        graphHasSubgraph,
      });
      if (validity === 'invalidAndHidden') continue;

      if (
        validity === 'valid' &&
        info.requiresShipmentInContext &&
        !wouldChildSeeShipmentVariable(
          parentNodeId,
          info.protobufNodeDataKey,
          rfNodes,
          rfEdges
        )
      ) {
        validity = { invalidWithReason: 'No shipment available upstream' };
      }

      const unavailableReason =
        validity === 'valid'
          ? info.getPickerUnavailableReason?.(parentNodeId, {
              nodes: rfNodes,
              edges: rfEdges,
            })
          : undefined;
      if (unavailableReason) {
        validity = { invalidWithReason: unavailableReason };
      }

      const existing = grouped.get(info.category) || [];
      existing.push({ info, validity });
      grouped.set(info.category, existing);
    }

    return grouped;
  }, [parentNodeId, rfNodes, rfEdges]);

  // Order categories: TRIGGER first, then ACTION, then CONTROL_FLOW
  const categoryOrder = useMemo(
    () => [
      NodeCategory.CONTROL_FLOW,
      NodeCategory.ACTION,
      NodeCategory.NOTIFICATIONS,
    ],
    []
  );

  // Apply the search filter once, in category order. Drops any category with
  // no matches so downstream code (menu items, openKeys, top-hit selection)
  // can iterate the same list.
  const filteredByCategory = useMemo(() => {
    const needle = searchText.toLowerCase();
    return categoryOrder
      .map((category) => {
        const entries = groupedOptions.get(category) ?? [];
        const matches =
          needle === ''
            ? entries
            : entries.filter(({ info }) =>
                info.label.toLowerCase().includes(needle)
              );
        return { category, entries: matches };
      })
      .filter(({ entries }) => entries.length > 0);
  }, [groupedOptions, categoryOrder, searchText]);

  // Generate menu items for Ant Design Menu
  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    filteredByCategory.forEach(
      ({ category, entries: filteredEntries }, index) => {
        // Add divider before each category except first one
        if (index > 0) {
          items.push({
            type: 'divider',
          });
        }

        const children = filteredEntries.map(({ info, validity }) => {
          const disabledReason =
            typeof validity === 'object'
              ? validity.invalidWithReason
              : undefined;

          return {
            key: info.protobufNodeDataKey,
            label: (
              <span className={styles.entry}>
                <div
                  className={`lb-icon-wrapper ${ICON_BACKGROUND_CLASS[category] ?? styles.iconNotification}`}
                >
                  {info.getIcon(14)}
                </div>
                <Flex vertical>
                  <CustomShipmentText text={info?.label} fontWeight={600} />
                  {disabledReason && (
                    <span className={styles.reason}>{disabledReason}</span>
                  )}
                </Flex>
              </span>
            ),
            disabled: !!disabledReason,
          };
        });

        items.push({
          key: category,
          label: (
            <span className={styles.categoryLabel}>
              <CustomShipmentText
                text={`${getCategoryLabel(category)} (${filteredEntries?.length})`}
                fontWeight={600}
                color={'var(--text-secondary)'}
              />
            </span>
          ),
          children,
        });
      }
    );

    // Add "No data found" message if no results
    if (searchText !== '' && filteredByCategory.length === 0) {
      items.push({
        key: 'no-data',
        label: <div className={styles.noData}>No data found</div>,
        disabled: true,
        className: styles.noDataItem,
      });
    }

    return items;
  }, [filteredByCategory, searchText]);

  // While the search box is empty, the user controls which groups are open
  // (starting with the first category). While a filter is active, this is
  // overridden to expand every category with hits.
  const [manualOpenGroupKeys, setManualOpenGroupKeys] = useState<string[]>(
    categoryOrder.length > 0 ? [categoryOrder[0]] : []
  );

  const openGroupKeys =
    searchText === ''
      ? manualOpenGroupKeys
      : filteredByCategory.map(({ category }) => category);

  // Every selectable row, in display order, across all categories. Disabled
  // and hidden rows are skipped. Drives both the highlight and arrow-key
  // movement, whether or not a filter is active.
  const navigableKeys = useMemo(() => {
    const keys: string[] = [];
    for (const { entries } of filteredByCategory) {
      for (const { info, validity } of entries) {
        if (validity === 'valid') {
          keys.push(info.protobufNodeDataKey);
        }
      }
    }
    return keys;
  }, [filteredByCategory]);

  // The row that Enter commits and arrow keys move. Highlighted via the menu's
  // selectedKeys.
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  // While filtering, highlight the first match so Enter can commit it. With no
  // filter, start with nothing highlighted; the first arrow press selects a
  // row. Re-runs when the filter text changes, discarding any arrow-driven
  // highlight left over from the previous query.
  useEffect(() => {
    setHighlightedKey(searchText === '' ? null : (navigableKeys[0] ?? null));
  }, [navigableKeys, searchText]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    onSelect(key as NodeType);
    onClose();
  };

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    // Persist toggles only when the filter is inactive; otherwise the
    // filter-driven openKeys would race with user clicks on group headers.
    if (searchText === '') {
      setManualOpenGroupKeys(keys as string[]);
    }
  };

  const handlePressEnter = () => {
    if (highlightedKey) {
      onSelect(highlightedKey as NodeType);
      onClose();
    }
  };

  // Move the highlight up/down through the filtered results, stopping at either
  // end. Bubbles up from the focused search box.
  const handleArrowNavigation = (event: ReactKeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    if (navigableKeys.length === 0) {
      return;
    }
    event.preventDefault();
    const currentIndex = highlightedKey
      ? navigableKeys.indexOf(highlightedKey)
      : -1;
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = Math.min(
      navigableKeys.length - 1,
      Math.max(0, currentIndex + delta)
    );
    const nextKey = navigableKeys[nextIndex];
    setHighlightedKey(nextKey);

    // With no filter, groups may be collapsed; open the one holding the newly
    // highlighted row so it stays visible. While filtering, every group with a
    // hit is already open.
    if (searchText === '') {
      const { category } = getNodeInfoForType(nextKey as NodeType);
      setManualOpenGroupKeys((prev) =>
        prev.includes(category) ? prev : [...prev, category]
      );
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Keep the highlighted row visible as arrow keys move it through the list.
  useEffect(() => {
    if (!highlightedKey) return;
    const selectedRow = panelRef.current?.querySelector(
      '.ant-menu-item-selected'
    );
    selectedRow?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedKey]);

  // Where the picker sits is only known at run time, so it is set on the element.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !anchorPosition) return;
    panel.style.left = `${anchorPosition.x}px`;
    panel.style.top = `${anchorPosition.y}px`;
    panel.style.transform = anchorPosition.alignRight
      ? 'translateX(-100%)'
      : '';
  }, [open, anchorPosition]);

  if (!open) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      onKeyDown={handleArrowNavigation}
      className={anchorPosition ? styles.anchored : undefined}
    >
      <div className='node-picker'>
        <SearchInput
          placeholder='Search...'
          autoFocus
          onChange={(v: string) => setSearchText(v?.toLowerCase() ?? '')}
          onPressEnter={handlePressEnter}
          width='100%'
          onClear={() => setSearchText('')}
        />

        <div className='node-picker-divider' />

        <Menu
          mode='inline'
          items={menuItems}
          onClick={handleMenuClick}
          openKeys={openGroupKeys}
          onOpenChange={handleOpenChange}
          selectedKeys={highlightedKey ? [highlightedKey] : []}
        />
      </div>
    </div>
  );
}
